import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChildRunApproval } from '../../../contracts/child-run-approval.types';
import type { ChatItem, LLMStreamEvent } from '../../../contracts/harness.types';
import type { RunTransport, StreamHeadersFactory } from '../../../contracts/transport';
import { parsePendingChildRunApproval } from '../../harness/approvals/parse-child-run-approval';
import { useRunStreamLifecycle } from '../../query/useRunStreamLifecycle';
import {
  deriveComputerUseProgress,
  MAX_TRACE_ENTRIES,
} from '../lib/computer-use-trace.mapper';
import type { DelegateTraceEntry } from '../lib/computer-use-trace.types';
import { selectLiveStepEntry } from './selectLiveStep';

export interface SubAgentComputerUseProgress {
  liveStep: string | undefined;
  liveStepCount: number;
  liveToolName: string | undefined;
  trace: DelegateTraceEntry[];
  /**
   * Latched `turn_cancelled` from the child stream, OR'd with the persisted
   * cancellation marker in the timeline. Survives the stream teardown so a
   * consumer (e.g. cua-status beat derivation) can still read "this child was
   * cancelled" after the hook stops streaming.
   */
  sawCancellation: boolean;
  /** Newest pending approval the child surfaces render. */
  childRunApproval: ChildRunApproval | null;
}

const EMPTY_PROGRESS: SubAgentComputerUseProgress = {
  liveStep: undefined,
  liveStepCount: 0,
  liveToolName: undefined,
  trace: [],
  sawCancellation: false,
  childRunApproval: null,
};

/**
 * Derive the computer-use step ladder from the child sub-agent run's HYDRATED
 * timeline (persisted `run.messages` + live tail) via `useRunStreamLifecycle` —
 * the same hydrate-then-tail model every other harness viewer uses. Replaces
 * the old live-only accumulator so both the projects chat block and the
 * Rust-owned `/cua-status` window converge regardless of when each mounts
 * (AGE-1095), remounts rebuild instead of reset, and a completed ladder
 * survives the stream ending.
 */
export const useSubAgentComputerUseProgress = (
  childAgentRunId: string | undefined,
  transport: RunTransport,
  {
    streamHeaders,
  }: {
    /**
     * The Scribe header factory, used when the transport carries none — the
     * same fallback the `.` door's lifecycle wrapper applies.
     */
    streamHeaders: StreamHeadersFactory;
  }
): SubAgentComputerUseProgress => {
  const [cancelLatched, setCancelLatched] = useState(false);
  const atCacheRef = useRef<Map<string, number>>(new Map());

  const onStreamEvent = useCallback((_id: string, event: LLMStreamEvent) => {
    // setState bails out when already `true`, so no guard ref is needed.
    if (event.type === 'turn_cancelled') setCancelLatched(true);
  }, []);

  // `follow: true` is load-bearing: without it the lifecycle closes the stream
  // between turns and stops polling once `input_state` is `ready`, so a late /
  // racing viewer (esp. the separately-mounted `/cua-status` window) caches
  // `ready` and goes permanently deaf — the same race ProjectsBackgroundRunner
  // documents.
  const { run, items } = useRunStreamLifecycle(childAgentRunId, transport, {
    onStreamEvent,
    follow: true,
    streamHeaders: transport.streamHeaders ?? streamHeaders,
  });

  // A persistent child accepts follow-up tasks as new turns on the same run
  // (Projects spawns a fresh child per task today), so reset the per-task
  // latch and timestamp cache on either a run OR a turn change.
  const turnKey = run?.latest_turn_id ?? null;
  useEffect(() => {
    setCancelLatched(false);
    atCacheRef.current = new Map();
  }, [childAgentRunId, turnKey]);

  // Handover guard. On an A→B switch the lifecycle resets its live overlay in an
  // effect, so for one commit `items` can still hold run A's overlay while the
  // hook already receives B. Only surface once the id has survived one committed
  // render (`committedId`, updated in an effect so it lags exactly that window)
  // AND the lifecycle's own run matches the requested id. `committedId` is state,
  // not a ref, so the effect re-render reliably lifts the suppression.
  const [committedId, setCommittedId] = useState(childAgentRunId);
  useEffect(() => {
    setCommittedId(childAgentRunId);
  }, [childAgentRunId]);
  const settled =
    !!childAgentRunId &&
    committedId === childAgentRunId &&
    run?.agent_run_id === childAgentRunId;

  const derived = useMemo(() => deriveComputerUseProgress(items, run), [items, run]);
  const childRunApproval = useMemo(
    () => findLatestChildRunApproval(items, childAgentRunId),
    [items, childAgentRunId]
  );

  // Memoized so `trace` keeps a stable identity across unrelated consumer
  // re-renders (both consumers re-render on every stream tick).
  return useMemo(() => {
    if (!settled) return EMPTY_PROGRESS;

    // Layer a stable, id-keyed `at` onto each entry (cosmetic HH:MM:SS in the
    // expandable panel) so a step keeps its first-seen time across re-derives.
    const cache = atCacheRef.current;
    const withAt = derived.entries.map((entry) => {
      const at = cache.get(entry.id) ?? Date.now();
      cache.set(entry.id, at);
      return { ...entry, at };
    });
    const trace =
      withAt.length > MAX_TRACE_ENTRIES
        ? withAt.slice(withAt.length - MAX_TRACE_ENTRIES)
        : withAt;

    const liveEntry = selectLiveStepEntry(trace);
    return {
      liveStep: liveEntry?.label,
      liveStepCount: derived.stepCount,
      liveToolName: liveEntry?.toolName,
      trace,
      sawCancellation: cancelLatched || derived.sawCancellation,
      childRunApproval,
    };
  }, [settled, derived, cancelLatched, childRunApproval]);
};

const findLatestChildRunApproval = (
  items: ChatItem[],
  agentRunId: string | undefined
): ChildRunApproval | null => {
  if (!agentRunId) return null;
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (item?.kind !== 'tool_use') continue;
    const approval = parsePendingChildRunApproval(item, agentRunId);
    if (approval) return approval;
  }
  return null;
};
